/**
 * Sell Your Jewellery enquiry form (sections/sell-enquiry.liquid).
 *
 * Talks to the Sell API Worker:
 *   POST /v1/drafts                          Turnstile token -> { draftId, token, expiresAt }
 *   PUT  /v1/drafts/:id/files/:slot          one photo or document per request
 *   DELETE /v1/drafts/:id/files/:slot/:file
 *   POST /v1/drafts/:id/submit               answers -> { reference }
 *
 * Answers, the draft and uploaded file ids are kept in localStorage so a
 * visitor can leave to photograph the piece and pick up where they left off.
 */
if (!customElements.get('sell-enquiry')) {
  const STORAGE_KEY = 'luminary:sell-enquiry:v1';
  const LAST_STEP = 5;
  const MAX_EDGE = 2400;
  const THUMB_EDGE = 240;
  const MAX_BYTES = 15 * 1024 * 1024;
  const REQUIRED_PHOTOS = ['front', 'back', 'signature', 'details'];
  const SLOTS = ['front', 'back', 'signature', 'details', 'other', 'documents', 'box'];
  const TURNSTILE_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

  const MESSAGES = {
    brand: 'Please choose the brand from the list.',
    pieceType: 'Please select the type of jewellery.',
    description: 'Please provide a short description.',
    photo: 'Please upload this photograph.',
    paperwork: 'Please select any paperwork you have, or “No paperwork”.',
    hasBox: 'Please let us know whether you have the box or packaging.',
    box: 'Please upload a photograph of the box or packaging.',
    condition: 'Please describe the condition.',
    altered: 'Please let us know whether the piece has been altered.',
    alterationDetails: 'Please tell us what was changed.',
    firstName: 'Please enter your first name.',
    lastName: 'Please enter your last name.',
    email: 'Please enter a valid email address.',
    phone: 'Please enter a valid telephone number.',
    location: 'Please enter your town or postcode.',
    contactMethod: 'Please choose how you would like to be contacted.',
    uploading: 'Please wait for your files to finish uploading.',
    unsupported: 'This file type isn’t supported. Please upload a JPEG, PNG or HEIC photograph.',
    unsupportedDocument: 'This file type isn’t supported. Please upload a photograph or PDF.',
    tooLarge: 'This file is too large. The maximum size is 15 MB.',
    uploadFailed: 'This file didn’t upload. Please try again.',
    expired: 'Your earlier uploads have expired. Please upload your photographs again.',
    notConnected: 'This form isn’t connected yet. Set the Sell API URL and Turnstile site key in the theme editor.',
    failed: 'Something went wrong sending your enquiry. Please try again in a moment.',
  };

  const normalise = (value) =>
    value
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '');

  const isHeic = (file) => /hei[cf]/i.test(file.type) || /\.hei[cf]$/i.test(file.name);
  const isPdf = (file) => file.type === 'application/pdf' || /\.pdf$/i.test(file.name);

  function emptyFiles() {
    return Object.fromEntries(SLOTS.map((slot) => [slot, []]));
  }

  async function loadImage(file) {
    const url = URL.createObjectURL(file);
    try {
      const image = new Image();
      image.src = url;
      await image.decode();
      return image;
    } finally {
      URL.revokeObjectURL(url);
    }
  }

  // Drawing to a canvas applies the EXIF orientation and drops all metadata,
  // including the GPS location phones embed in photographs.
  function resize(image, maxEdge, quality) {
    const scale = Math.min(1, maxEdge / Math.max(image.naturalWidth, image.naturalHeight));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(image.naturalWidth * scale);
    canvas.height = Math.round(image.naturalHeight * scale);
    canvas.getContext('2d').drawImage(image, 0, 0, canvas.width, canvas.height);
    return canvas;
  }

  async function prepareFile(file, allowPdf) {
    if (allowPdf && isPdf(file)) return { blob: file, thumb: null };

    let image;
    try {
      image = await loadImage(file);
    } catch (error) {
      // Browsers other than Safari can't decode HEIC; the API accepts it as-is.
      if (isHeic(file)) return { blob: file, thumb: null };
      throw new Error(allowPdf ? 'unsupportedDocument' : 'unsupported');
    }

    const blob = await new Promise((resolve) => resize(image, MAX_EDGE).toBlob(resolve, 'image/jpeg', 0.85));
    if (!blob) throw new Error('unsupported');
    const thumb = resize(image, THUMB_EDGE).toDataURL('image/jpeg', 0.7);
    return { blob, thumb };
  }

  let turnstileScript;
  function loadTurnstile() {
    if (window.turnstile) return Promise.resolve(window.turnstile);
    turnstileScript ||= new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = TURNSTILE_SRC;
      script.async = true;
      script.onload = () => resolve(window.turnstile);
      script.onerror = () => reject(new Error('turnstile_unavailable'));
      document.head.appendChild(script);
    });
    return turnstileScript;
  }

  class SellEnquiry extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;

      this.apiUrl = (this.dataset.apiUrl || '').replace(/\/$/, '');
      this.siteKey = this.dataset.turnstileSiteKey || '';
      this.form = this.querySelector('[data-form]');
      this.steps = Array.from(this.querySelectorAll('[data-step]'));
      this.progressItems = Array.from(this.querySelectorAll('[data-progress-step]'));
      this.mobileProgress = this.querySelector('[data-progress-mobile]');
      this.alert = this.querySelector('[data-form-alert]');
      this.backButton = this.querySelector('[data-back]');
      this.nextButton = this.querySelector('[data-next]');
      this.nextLabel = this.querySelector('[data-next-label]');
      this.submitLabel = this.querySelector('[data-submit-label]').content.textContent.trim();
      this.reassurance = this.querySelector('[data-reassurance]');
      this.pending = Object.fromEntries(SLOTS.map((slot) => [slot, []]));

      this.state = this.loadState();
      this.restoreAnswers();
      this.initCombobox();
      this.initUploads();
      this.updateConditionals();

      this.form.addEventListener('submit', this.handleNext.bind(this));
      this.form.addEventListener('change', this.handleChange.bind(this));
      this.form.addEventListener('input', () => this.saveAnswers());
      this.backButton.addEventListener('click', () => this.showStep(this.state.step - 1));

      this.showStep(this.state.step, { focus: false });
      if (this.dataset.designMode !== undefined && (!this.apiUrl || !this.siteKey)) {
        this.showAlert(MESSAGES.notConnected);
      }
    }

    // ---- State ---------------------------------------------------------------

    loadState() {
      const fresh = { step: 1, answers: {}, draft: null, files: emptyFiles() };
      let stored;
      try {
        stored = JSON.parse(localStorage.getItem(STORAGE_KEY));
      } catch (error) {
        return fresh;
      }
      if (!stored) return fresh;

      const state = { ...fresh, ...stored, files: { ...emptyFiles(), ...stored.files } };
      if (state.draft && Date.parse(state.draft.expiresAt) < Date.now()) {
        const hadFiles = SLOTS.some((slot) => state.files[slot].length);
        state.draft = null;
        state.files = emptyFiles();
        this.expiredNotice = hadFiles;
      }
      state.step = Math.min(Math.max(Number(state.step) || 1, 1), LAST_STEP);
      return state;
    }

    saveState() {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(this.state));
      } catch (error) {
        // Storage full or blocked: retry without thumbnails, then give up quietly.
        try {
          const files = Object.fromEntries(
            SLOTS.map((slot) => [slot, this.state.files[slot].map((file) => ({ ...file, thumb: null }))]),
          );
          localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...this.state, files }));
        } catch (ignored) {
          /* draft just won't survive a reload */
        }
      }
    }

    clearState() {
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch (error) {
        /* ignore */
      }
    }

    readAnswers() {
      const data = new FormData(this.form);
      const value = (name) => (data.get(name) || '').toString().trim();
      return {
        brand: value('brand'),
        pieceType: value('pieceType'),
        description: value('description'),
        paperwork: data.getAll('paperwork'),
        hasBox: value('hasBox'),
        condition: value('condition'),
        altered: value('altered'),
        alterationDetails: value('alterationDetails'),
        firstName: value('firstName'),
        lastName: value('lastName'),
        email: value('email'),
        phone: value('phone'),
        location: value('location'),
        contactMethod: value('contactMethod'),
        marketingOptIn: data.get('marketingOptIn') === 'yes',
      };
    }

    saveAnswers() {
      this.state.answers = this.readAnswers();
      this.saveState();
    }

    restoreAnswers() {
      const answers = this.state.answers || {};
      Object.entries(answers).forEach(([name, value]) => {
        this.form.querySelectorAll(`[name="${name}"]`).forEach((control) => {
          if (control.type === 'radio') control.checked = control.value === value;
          else if (control.type === 'checkbox' && typeof value === 'boolean') control.checked = value;
          else if (control.type === 'checkbox') control.checked = Array.isArray(value) && value.includes(control.value);
          else control.value = value ?? '';
        });
      });
      if (answers.brand) this.querySelector('[data-combobox-input]').value = answers.brand;
    }

    // ---- Steps ---------------------------------------------------------------

    showStep(step, { focus = true } = {}) {
      this.state.step = step;
      this.saveState();
      this.hideAlert();

      this.steps.forEach((section) => {
        section.hidden = Number(section.dataset.step) !== step;
      });
      this.updateProgress(step);

      this.backButton.hidden = step === 1;
      this.nextLabel.textContent = step === LAST_STEP ? this.submitLabel : 'Continue';
      if (this.reassurance) this.reassurance.hidden = step !== LAST_STEP;
      if (step === 2 && this.expiredNotice) this.showAlert(MESSAGES.expired);

      if (focus) {
        this.scrollToTop();
        this.querySelector(`[data-step="${step}"] .sell-step__title`).focus({ preventScroll: true });
      }
    }

    updateProgress(step) {
      this.progressItems.forEach((item) => {
        const itemStep = Number(item.dataset.progressStep);
        item.classList.toggle('is-complete', itemStep < step);
        item.classList.toggle('is-current', itemStep === step);
        if (itemStep === step) item.setAttribute('aria-current', 'step');
        else item.removeAttribute('aria-current');
      });
      const current = this.progressItems[step - 1];
      if (this.mobileProgress && current) {
        const label = current.querySelector('.sell-progress__label').textContent;
        this.mobileProgress.textContent = step > LAST_STEP ? label : `Step ${step} of ${LAST_STEP} · ${label}`;
      }
    }

    scrollToTop() {
      const top = this.getBoundingClientRect().top;
      if (top < 0) window.scrollTo({ top: window.scrollY + top - 100, behavior: 'smooth' });
    }

    handleNext(event) {
      event.preventDefault();
      this.saveAnswers();
      if (!this.validateStep(this.state.step)) return;
      if (this.state.step < LAST_STEP) this.showStep(this.state.step + 1);
      else this.submit();
    }

    handleChange(event) {
      const { target } = event;
      if (target.name === 'paperwork' && target.checked) {
        // "No paperwork" can't be combined with anything else.
        this.form.querySelectorAll('[name="paperwork"]').forEach((box) => {
          if (box !== target && (target.value === 'none' || box.value === 'none')) box.checked = false;
        });
      }
      if (target.name) this.clearError(target.name);
      this.updateConditionals();
      this.saveAnswers();
    }

    updateConditionals() {
      const answers = this.readAnswers();
      const visible = {
        hasPaperwork: answers.paperwork.some((code) => code !== 'none'),
        hasBox: answers.hasBox === 'yes',
        altered: answers.altered === 'yes',
      };
      this.querySelectorAll('[data-show-when]').forEach((element) => {
        element.hidden = !visible[element.dataset.showWhen];
      });
    }

    // ---- Validation ----------------------------------------------------------

    validateStep(step) {
      const answers = this.readAnswers();
      const errors = {};
      const check = (name, valid = Boolean(answers[name])) => {
        if (!valid) errors[name] = MESSAGES[name];
      };

      if (step === 1) {
        check('brand');
        check('pieceType');
        check('description');
      } else if (step === 2) {
        REQUIRED_PHOTOS.forEach((slot) => {
          if (!this.state.files[slot].length) errors[`files.${slot}`] = MESSAGES.photo;
        });
      } else if (step === 3) {
        check('paperwork', answers.paperwork.length > 0);
        check('hasBox');
        if (answers.hasBox === 'yes' && !this.state.files.box.length) errors['files.box'] = MESSAGES.box;
      } else if (step === 4) {
        check('condition');
        check('altered');
        if (answers.altered === 'yes') check('alterationDetails');
      } else if (step === 5) {
        check('firstName');
        check('lastName');
        check('email', /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(answers.email));
        check('phone', /^\+?[\d\s().-]{7,}$/.test(answers.phone));
        check('location');
        check('contactMethod');
      }

      const section = this.querySelector(`[data-step="${step}"]`);
      section.querySelectorAll('[data-error-for]').forEach((element) => this.clearError(element.dataset.errorFor));
      Object.entries(errors).forEach(([field, message]) => this.setError(field, message));

      if (this.uploadsInProgress(step)) {
        this.showAlert(MESSAGES.uploading);
        return false;
      }
      if (Object.keys(errors).length) {
        this.focusField(Object.keys(errors)[0]);
        return false;
      }
      return true;
    }

    setError(field, message) {
      const error = this.querySelector(`[data-error-for="${field}"]`);
      if (error) error.textContent = message;
      this.querySelector(`[data-field="${field}"]`)?.classList.add('has-error');
      this.querySelectorAll(`[name="${field}"], [data-field="${field}"] [data-combobox-input]`).forEach((control) =>
        control.setAttribute('aria-invalid', 'true'),
      );
    }

    clearError(field) {
      const error = this.querySelector(`[data-error-for="${field}"]`);
      if (error) error.textContent = '';
      this.querySelector(`[data-field="${field}"]`)?.classList.remove('has-error');
      this.querySelectorAll(`[name="${field}"], [data-field="${field}"] [data-combobox-input]`).forEach((control) =>
        control.removeAttribute('aria-invalid'),
      );
    }

    focusField(field) {
      const container = this.querySelector(`[data-field="${field}"]`);
      const control = container?.querySelector('input:not([type=hidden]):not([type=file]), textarea, label.btn');
      container?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      (control?.matches('label') ? control.querySelector('input') : control)?.focus({ preventScroll: true });
    }

    showAlert(message) {
      this.alert.textContent = message;
      this.alert.hidden = false;
    }

    hideAlert() {
      this.alert.hidden = true;
      this.alert.textContent = '';
    }

    // ---- Brand combobox --------------------------------------------------------

    initCombobox() {
      const input = this.querySelector('[data-combobox-input]');
      const value = this.querySelector('[data-combobox-value]');
      const listbox = this.querySelector('[data-combobox-listbox]');
      const empty = this.querySelector('[data-combobox-empty]');
      const options = Array.from(listbox.querySelectorAll('[role="option"]'));
      let active = -1;

      const visibleOptions = () => options.filter((option) => !option.hidden);

      const setActive = (index) => {
        const visible = visibleOptions();
        options.forEach((option) => option.classList.remove('is-active'));
        active = visible.length ? (index + visible.length) % visible.length : -1;
        if (active >= 0) {
          visible[active].classList.add('is-active');
          visible[active].scrollIntoView({ block: 'nearest' });
          input.setAttribute('aria-activedescendant', visible[active].id);
        } else {
          input.removeAttribute('aria-activedescendant');
        }
      };

      const open = () => {
        listbox.hidden = visibleOptions().length === 0;
        input.setAttribute('aria-expanded', String(!listbox.hidden));
      };

      const close = () => {
        listbox.hidden = true;
        input.setAttribute('aria-expanded', 'false');
        setActive(-1);
      };

      const filter = () => {
        const query = normalise(input.value);
        options.forEach((option) => {
          option.hidden = Boolean(query) && !normalise(option.dataset.value).includes(query);
        });
        empty.hidden = !query || visibleOptions().length > 0;
      };

      const select = (option) => {
        input.value = option.dataset.value;
        value.value = option.dataset.value;
        options.forEach((item) => item.setAttribute('aria-selected', String(item === option)));
        empty.hidden = true;
        close();
        this.clearError('brand');
        this.saveAnswers();
      };

      // Accept a typed name that exactly matches a brand (ignoring accents and punctuation).
      const selectExactMatch = () => {
        const match = options.find((option) => normalise(option.dataset.value) === normalise(input.value));
        if (match) select(match);
      };

      if (value.value) {
        const current = options.find((option) => option.dataset.value === value.value);
        if (current) current.setAttribute('aria-selected', 'true');
      }

      input.addEventListener('input', () => {
        value.value = '';
        filter();
        open();
        setActive(normalise(input.value) ? 0 : -1);
        this.saveAnswers();
      });
      input.addEventListener('focus', () => {
        filter();
        open();
      });
      input.addEventListener('blur', () => {
        selectExactMatch();
        close();
      });
      input.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          event.preventDefault();
          if (listbox.hidden) open();
          setActive(active + (event.key === 'ArrowDown' ? 1 : -1));
        } else if (event.key === 'Enter') {
          const visible = visibleOptions();
          if (!listbox.hidden && active >= 0 && visible[active]) {
            event.preventDefault();
            select(visible[active]);
          } else if (!value.value) {
            event.preventDefault();
            selectExactMatch();
          }
        } else if (event.key === 'Escape' && !listbox.hidden) {
          event.preventDefault();
          close();
        }
      });
      listbox.addEventListener('mousedown', (event) => event.preventDefault());
      listbox.addEventListener('click', (event) => {
        const option = event.target.closest('[role="option"]');
        if (option) select(option);
      });
    }

    // ---- Uploads ---------------------------------------------------------------

    initUploads() {
      this.slotElements = {};
      this.querySelectorAll('[data-upload-slot]').forEach((element) => {
        const slot = element.dataset.uploadSlot;
        this.slotElements[slot] = element;
        element.querySelector('[data-upload-input]').addEventListener('change', (event) => {
          const files = Array.from(event.target.files);
          event.target.value = '';
          this.addFiles(slot, files);
        });
        element.addEventListener('click', (event) => {
          const remove = event.target.closest('[data-remove-file]');
          if (remove) this.removeFile(slot, remove.dataset.removeFile);
        });
        this.renderSlot(slot);
      });
    }

    addFiles(slot, files) {
      const element = this.slotElements[slot];
      const max = Number(element.dataset.max || 1);
      const multiple = max > 1;
      const room = multiple ? max - this.state.files[slot].length - this.activePending(slot).length : 1;
      files.slice(0, Math.max(room, 0)).forEach((file) => this.uploadFile(slot, file, multiple));
    }

    activePending(slot) {
      return this.pending[slot].filter((item) => !item.error);
    }

    uploadsInProgress(step) {
      const section = this.querySelector(`[data-step="${step}"]`);
      return Array.from(section.querySelectorAll('[data-upload-slot]')).some(
        (element) => this.activePending(element.dataset.uploadSlot).length > 0,
      );
    }

    async uploadFile(slot, file, multiple) {
      const item = { id: crypto.randomUUID(), name: file.name, progress: 0, status: 'Preparing…', error: null };
      if (!multiple) this.pending[slot] = [];
      this.pending[slot].push(item);
      this.clearError(`files.${slot}`);
      this.hideAlert();
      this.renderSlot(slot);

      const fail = (message) => {
        item.error = message;
        this.renderSlot(slot);
      };

      if (!this.apiUrl || !this.siteKey) return fail(MESSAGES.notConnected);

      let prepared;
      try {
        prepared = await prepareFile(file, slot === 'documents');
      } catch (error) {
        return fail(MESSAGES[error.message] || MESSAGES.unsupported);
      }
      if (prepared.blob.size > MAX_BYTES) return fail(MESSAGES.tooLarge);

      try {
        let response = await this.putFile(slot, prepared.blob, item);
        if (response.status === 401) {
          // Draft expired since the page loaded: start a fresh one and retry once.
          this.resetDraft();
          response = await this.putFile(slot, prepared.blob, item);
        }
        if (!this.pending[slot].includes(item)) {
          // Removed or replaced while uploading.
          if (response.status === 201) this.deleteRemote(slot, response.body.fileId);
          return;
        }
        if (response.status !== 201) {
          return fail(response.body?.error === 'unsupported_file_type' ? MESSAGES.unsupported : MESSAGES.uploadFailed);
        }

        const uploaded = {
          fileId: response.body.fileId,
          name: file.name,
          contentType: response.body.contentType,
          thumb: prepared.thumb,
        };
        this.state.files[slot] = multiple ? [...this.state.files[slot], uploaded] : [uploaded];
        this.pending[slot] = this.pending[slot].filter((entry) => entry !== item);
        this.expiredNotice = false;
        this.saveState();
        this.renderSlot(slot);
      } catch (error) {
        fail(MESSAGES.uploadFailed);
      }
    }

    async putFile(slot, blob, item) {
      const draft = await this.ensureDraft();
      item.status = 'Uploading…';
      this.renderSlot(slot);

      return new Promise((resolve, reject) => {
        const request = new XMLHttpRequest();
        request.open('PUT', `${this.apiUrl}/v1/drafts/${draft.id}/files/${slot}`);
        request.setRequestHeader('Authorization', `Bearer ${draft.token}`);
        request.setRequestHeader('Content-Type', blob.type || 'application/octet-stream');
        request.upload.addEventListener('progress', (event) => {
          if (!event.lengthComputable) return;
          item.progress = Math.round((event.loaded / event.total) * 100);
          item.status = `Uploading ${item.progress}%`;
          this.renderSlot(slot);
        });
        request.addEventListener('load', () => {
          let body = null;
          try {
            body = JSON.parse(request.responseText);
          } catch (error) {
            /* empty or non-JSON body */
          }
          resolve({ status: request.status, body });
        });
        request.addEventListener('error', () => reject(new Error('network')));
        request.send(blob);
      });
    }

    removeFile(slot, fileId) {
      const pendingIndex = this.pending[slot].findIndex((item) => item.id === fileId);
      if (pendingIndex >= 0) {
        this.pending[slot].splice(pendingIndex, 1);
      } else {
        this.state.files[slot] = this.state.files[slot].filter((file) => file.fileId !== fileId);
        this.saveState();
        this.deleteRemote(slot, fileId);
      }
      this.renderSlot(slot);
    }

    deleteRemote(slot, fileId) {
      const { draft } = this.state;
      if (!draft) return;
      fetch(`${this.apiUrl}/v1/drafts/${draft.id}/files/${slot}/${fileId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${draft.token}` },
      }).catch(() => {});
    }

    renderSlot(slot) {
      const element = this.slotElements?.[slot];
      if (!element) return;
      const uploaded = this.state.files[slot];
      const pending = this.pending[slot];
      const max = Number(element.dataset.max || 1);
      const status = element.querySelector('[data-upload-status]');
      const buttonLabel = element.querySelector('[data-upload-button-label]');
      const button = element.querySelector('[data-upload-button]');

      if (max === 1) {
        const current = pending[0];
        const file = uploaded[0];
        const thumb = element.querySelector('[data-upload-thumb]');
        thumb.style.backgroundImage = !current && file?.thumb ? `url("${file.thumb}")` : '';
        thumb.classList.toggle('has-file', Boolean(file) && !current);
        thumb.classList.toggle('is-uploading', Boolean(current) && !current.error);
        status.textContent = current ? current.error || current.status : file ? 'Uploaded' : '';
        status.classList.toggle('is-error', Boolean(current?.error));
        buttonLabel.textContent = file || current ? 'Replace' : 'Upload photo';
        // The API replaces a single photo on every upload, so one at a time.
        const busy = Boolean(current) && !current.error;
        button.classList.toggle('is-disabled', busy);
        button.querySelector('input').disabled = busy;
        element.classList.toggle('is-complete', Boolean(file) && !current);
        return;
      }

      const list = element.querySelector('[data-upload-files]');
      list.replaceChildren(
        ...uploaded.map((file) => this.fileRow(file.fileId, file.name, 'Uploaded', false, file.thumb)),
        ...pending.map((item) => this.fileRow(item.id, item.name, item.error || item.status, Boolean(item.error), null)),
      );
      const full = uploaded.length + this.activePending(slot).length >= max;
      button.classList.toggle('is-disabled', full);
      button.querySelector('input').disabled = full;
      buttonLabel.textContent = uploaded.length ? 'Add another' : 'Add files';
      status.textContent = full ? `Maximum of ${max} files` : '';
    }

    fileRow(id, name, statusText, isError, thumb) {
      const row = document.createElement('li');
      row.className = 'sell-upload__file';

      const preview = document.createElement('span');
      preview.className = 'sell-upload__file-thumb';
      preview.setAttribute('aria-hidden', 'true');
      if (thumb) preview.style.backgroundImage = `url("${thumb}")`;

      const text = document.createElement('span');
      text.className = 'sell-upload__file-text';
      const fileName = document.createElement('span');
      fileName.className = 'sell-upload__file-name';
      fileName.textContent = name;
      const fileStatus = document.createElement('span');
      fileStatus.className = `sell-upload__status${isError ? ' is-error' : ''}`;
      fileStatus.textContent = statusText;
      text.append(fileName, fileStatus);

      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'small-feature-link sell-upload__remove';
      remove.dataset.removeFile = id;
      remove.textContent = 'Remove';
      remove.setAttribute('aria-label', `Remove ${name}`);

      row.append(preview, text, remove);
      return row;
    }

    // ---- Draft + Turnstile -------------------------------------------------------

    ensureDraft() {
      const { draft } = this.state;
      if (draft && Date.parse(draft.expiresAt) - Date.now() > 5 * 60 * 1000) return Promise.resolve(draft);

      this.draftPromise ||= (async () => {
        const turnstileToken = await this.getTurnstileToken();
        const response = await fetch(`${this.apiUrl}/v1/drafts`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ turnstileToken }),
        });
        if (!response.ok) throw new Error('draft_failed');
        const { draftId, token, expiresAt } = await response.json();
        this.state.draft = { id: draftId, token, expiresAt };
        this.saveState();
        return this.state.draft;
      })().finally(() => {
        this.draftPromise = null;
      });
      return this.draftPromise;
    }

    resetDraft() {
      this.state.draft = null;
      this.state.files = emptyFiles();
      this.saveState();
      SLOTS.forEach((slot) => this.renderSlot(slot));
    }

    async getTurnstileToken() {
      const turnstile = await loadTurnstile();
      return new Promise((resolve, reject) => {
        this.turnstileCallbacks = { resolve, reject };
        if (this.turnstileWidget === undefined) {
          this.turnstileWidget = turnstile.render(this.querySelector('[data-turnstile]'), {
            sitekey: this.siteKey,
            action: 'sell_enquiry',
            execution: 'execute',
            appearance: 'interaction-only',
            callback: (token) => this.turnstileCallbacks?.resolve(token),
            'error-callback': () => this.turnstileCallbacks?.reject(new Error('turnstile_failed')),
          });
        } else {
          turnstile.reset(this.turnstileWidget);
        }
        turnstile.execute(this.turnstileWidget);
      });
    }

    // ---- Submit ------------------------------------------------------------------

    async submit() {
      const answers = this.readAnswers();
      const payload = {
        ...answers,
        hasBox: answers.hasBox === 'yes',
        alterationDetails: answers.altered === 'yes' ? answers.alterationDetails : undefined,
      };
      const { draft } = this.state;
      if (!draft) {
        this.resetDraft();
        this.showStep(2);
        this.showAlert(MESSAGES.expired);
        return;
      }

      this.nextButton.disabled = true;
      this.nextButton.setAttribute('aria-busy', 'true');
      this.nextLabel.textContent = 'Sending…';

      try {
        const response = await fetch(`${this.apiUrl}/v1/drafts/${draft.id}/submit`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${draft.token}` },
          body: JSON.stringify(payload),
        });
        const body = await response.json().catch(() => ({}));

        if (response.ok && body.reference) {
          this.showConfirmation(body.reference);
        } else if (response.status === 422 && body.fields) {
          this.showServerErrors(body.fields);
        } else if (response.status === 401) {
          this.resetDraft();
          this.showStep(2);
          this.showAlert(MESSAGES.expired);
        } else {
          this.showAlert(MESSAGES.failed);
        }
      } catch (error) {
        this.showAlert(MESSAGES.failed);
      } finally {
        this.nextButton.disabled = false;
        this.nextButton.removeAttribute('aria-busy');
        if (this.state.step === LAST_STEP) this.nextLabel.textContent = this.submitLabel;
      }
    }

    showServerErrors(fields) {
      const entries = Object.entries(fields).map(([field, messages]) => {
        const serverMessage = Array.isArray(messages) ? messages[0] : messages;
        const message = field.startsWith('files.')
          ? field === 'files.box'
            ? MESSAGES.box
            : MESSAGES.photo
          : MESSAGES[field] || serverMessage;
        return [field, message];
      });
      const steps = entries
        .map(([field]) => Number(this.querySelector(`[data-field="${field}"]`)?.closest('[data-step]')?.dataset.step))
        .filter(Boolean);
      if (steps.length) this.showStep(Math.min(...steps));
      entries.forEach(([field, message]) => this.setError(field, message));
      if (entries.length) this.focusField(entries[0][0]);
    }

    showConfirmation(reference) {
      this.clearState();
      this.form.hidden = true;
      this.updateProgress(LAST_STEP + 1);
      const done = this.querySelector('[data-done]');
      done.querySelector('[data-done-reference]').textContent = reference;
      done.hidden = false;
      this.scrollToTop();
      done.querySelector('[data-done-title]').focus({ preventScroll: true });
    }
  }

  customElements.define('sell-enquiry', SellEnquiry);
}
